import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../Snackbar/SnackbarContext";

const DiaryDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentNames, setStudentNames] = useState("");

  const role = localStorage.getItem("role") || "staff";
  const prefixPath = role === "admin" ? "/admin" : "/teacher";

  useEffect(() => {
    if (id) {
      fetchDiaryDetails();
    }
  }, [id]);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDiaryDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiEndpoints.student_diary}?guid=${id}`, {
        headers: getHeaders(),
      });
      if (res.data && res.data.status && res.data.data) {
        const d = res.data.data;
        setDiary(d);
        if (d.target_type === "students" && d.student_guids) {
          try {
            const studsRes = await axios.get(
              `${apiEndpoints.student_diary}?action=getStudents&class_guid=${d.class_guid}`,
              { headers: getHeaders() }
            );
            if (studsRes.data && studsRes.data.status) {
              const guidsList = d.student_guids.split(",");
              const names = studsRes.data.data
                .filter((s) => guidsList.includes(s.guid))
                .map((s) => s.name)
                .join(", ");
              setStudentNames(names);
            }
          } catch (stErr) {
            console.error("Error loading students for details:", stErr);
          }
        }
      } else {
        showSnackbar("Failed to fetch diary details", "error");
        navigate(`${prefixPath}/diary`);
      }
    } catch (err) {
      console.error("Error fetching diary details:", err);
      showSnackbar("Error loading diary details", "error");
      navigate(`${prefixPath}/diary`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!diary) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">Diary entry not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.95)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)"
        }}
      >
        {/* Header with back button */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton 
            onClick={() => navigate(`${prefixPath}/diary`)} 
            sx={{ 
              mr: 2, 
              backgroundColor: "rgba(63, 81, 181, 0.08)",
              color: "#3F51B5",
              "&:hover": {
                backgroundColor: "rgba(63, 81, 181, 0.15)",
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={700} color="#3F51B5">
            Student Diary Details
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={3}>
          {/* Metadata Section */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: "#F4F6F9",
                border: "1px solid #E2E8F0"
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Diary Date
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#2D3748">
                    {diary.date}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    School Branch
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#2D3748">
                    {diary.branch_name || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Target Class
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#2D3748">
                    {diary.class_name || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Target Audience
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#2D3748">
                    {diary.target_type === "students" ? "Specific Students" : "Whole Class"}
                  </Typography>
                </Grid>
                {diary.target_type === "students" && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Targeted Students
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="#2D3748">
                      {studentNames || "No students selected"}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          {/* Academic Log */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
              Academic Log
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: "#FCFDFE",
                minHeight: 120,
                border: "1px solid #E2E8F0"
              }}
            >
              <Typography style={{ whiteSpace: "pre-line", color: "#4A5568" }}>
                {diary.classwork || "No classwork logged."}
              </Typography>
            </Paper>
          </Grid>

          {/* Assignments */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
              Assignments
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: "#FCFDFE",
                minHeight: 120,
                border: "1px solid #E2E8F0"
              }}
            >
              <Typography style={{ whiteSpace: "pre-line", color: "#4A5568" }}>
                {diary.homework || "No homework assigned."}
              </Typography>
            </Paper>
          </Grid>

          {/* Future Activities */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
              Future Activities
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: "#FCFDFE",
                minHeight: 120,
                border: "1px solid #E2E8F0"
              }}
            >
              <Typography style={{ whiteSpace: "pre-line", color: "#4A5568" }}>
                {diary.tomorrow_activities || "No activities logged for tomorrow."}
              </Typography>
            </Paper>
          </Grid>

          {/* Teacher Guidance */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
              Teacher Guidance
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: "#FCFDFE",
                minHeight: 120,
                border: "1px solid #E2E8F0"
              }}
            >
              <Typography style={{ whiteSpace: "pre-line", color: "#4A5568" }}>
                {diary.teacher_remarks || "No teacher remarks logged."}
              </Typography>
            </Paper>
          </Grid>

          {/* Footer Actions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`${prefixPath}/diary`)}
              sx={{ borderRadius: 2, px: 4, boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)" }}
            >
              Close Details
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default DiaryDetail;
